import React, { useMemo, useState } from "react";
import { JPBReferenceProperty, AreaUnit, PriceMode, SoilQualityGroup, SOIL_QUALITY_GROUPS, YearGrouping, AverageCalculationMethod, JPBPriceIndexSource } from "./JPBState";
import { Localization } from "@viamap/viamap2-common";
import { formatPricePerSquareMeter, getSortedValuesForSeries, getStatisticsLabels, calculateSoilSpecificAgriculturalPrices, calculateWeightedStatisticsForSeries, formatDate, getResolvedJPBAssumptions, getSoilQualityFactorsByGroup, getNonAgricultureToAgricultureRatio } from "./JPBFunc";
import { autoUpdate, offset, useDismiss, useFloating, useHover, useInteractions } from "@floating-ui/react";
import { JPBManager } from "./JPBManager";
import { MIBIcon } from "src/componentsUtils/MIBIcon";
import { MITFloatingPortal } from "src/componentsUtils/PortalHelpers";

type SoilRowWithTooltipProps = {
   soilGroup: SoilQualityGroup;
   price: number;
   areaUnit: AreaUnit;
   tooltipText: string;
}

function SoilRowWithTooltip(props: SoilRowWithTooltipProps) {
   const [tooltipOpen, setTooltipOpen] = useState(false);
   const { refs, floatingStyles, context } = useFloating({
      whileElementsMounted: autoUpdate,
      open: tooltipOpen,
      middleware: [offset(5)],
      onOpenChange: setTooltipOpen,
   });
   const { getReferenceProps, getFloatingProps } = useInteractions([
      useHover(context),
      useDismiss(context)
   ]);
   return (
      <>
         <div className="jpb-stats-row" style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
            <div className="jpb-stats-row-label">
               <span style={{ display: "inline" }}>
                  <span style={{ fontWeight: "500", fontSize: "0.9em" }}>
                     {props.soilGroup} {props.areaUnit === AreaUnit.Hectare ? Localization.getText("price per ha") : Localization.getText("price per m²")}
                  </span>
                  <sup
                     ref={refs.setReference}
                     style={{ cursor: "help", marginLeft: "4px", lineHeight: 1, fontSize: "12px", color: "#666" }}
                     {...getReferenceProps()}
                  >
                     <MIBIcon icon="info" />
                  </sup>
               </span>
            </div>
            <div className="jpb-stats-row-values">
               <div style={{ textAlign: "right", minWidth: "80px" }}>
                  <div style={{ fontWeight: "600" }}>
                     {formatPricePerSquareMeter(props.price, props.areaUnit)}
                  </div>
               </div>
            </div>
         </div>
         {Boolean(tooltipOpen) && (
            <MITFloatingPortal>
               <div
                  ref={refs.setFloating}
                  className="ViewButtonToolTip assumptions-box"
                  style={{
                     ...floatingStyles,
                     maxWidth: "300px",
                     fontSize: "0.85em"
                  }}
                  {...getFloatingProps()}
               >
                  <div>
                     <div style={{ fontWeight: "bold", marginBottom: "4px" }}>
                        {props.soilGroup} {props.areaUnit === AreaUnit.Hectare ? Localization.getText("price per ha") : Localization.getText("price per m²")}
                     </div>
                     <div style={{ fontSize: "0.9em", lineHeight: "1.4" }}>{props.tooltipText}</div>
                  </div>
               </div>
            </MITFloatingPortal>
         )}
      </>
   );
}

type JPBStatisticsProps = {
   data: JPBReferenceProperty[];
   areaUnit: AreaUnit;
   priceMode: PriceMode;
   checkedItems: Set<string>;
   yearGrouping: YearGrouping;
   calculationDateTo?: Date;
   averageCalculationMethod: AverageCalculationMethod;
   priceIndexSource: JPBPriceIndexSource;
}

export function JPBStatistics(props: JPBStatisticsProps) {
   const assumptions = getResolvedJPBAssumptions();
   const sourceLabel = JPBManager.getPriceIndexSourceLabel(props.priceIndexSource);
   const isIndexApplied = props.priceIndexSource !== JPBPriceIndexSource.None;
   const indexedStatsLabel = props.areaUnit === AreaUnit.Hectare
      ? Localization.getText(isIndexApplied ? "Indexed price per ha" : "Price per ha")
      : Localization.getText(isIndexApplied ? "Indexed price per m²" : "Price per m²");
   const soilFactorsByGroup = getSoilQualityFactorsByGroup(assumptions);
   const nonAgriToAgriRatio = getNonAgricultureToAgricultureRatio(assumptions);
   const formatFactor = (value: number) => new Intl.NumberFormat(Localization.getLocale(), {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
      useGrouping: false
   }).format(value);
   const dynamicFactorTextArgs = useMemo(() => {
      const valuePct = assumptions.nonAgricultureFactor > 0
         ? (assumptions.agricultureFactor / assumptions.nonAgricultureFactor) * 100
         : Number.POSITIVE_INFINITY;
      return {
         agricultureFactor: formatFactor(assumptions.agricultureFactor),
         nonAgricultureFactor: formatFactor(assumptions.nonAgricultureFactor),
         ratioPct: formatFactor(nonAgriToAgriRatio * 100),
         valuePct: Number.isFinite(valuePct) ? formatFactor(valuePct) : "∞",
         baselineGroup: SoilQualityGroup.JB_5_6,
         baselineFactor: formatFactor(soilFactorsByGroup[SoilQualityGroup.JB_5_6] ?? 1),
      };
   }, [assumptions.agricultureFactor, assumptions.nonAgricultureFactor, nonAgriToAgriRatio, soilFactorsByGroup]);
   const soilTypesByGroup = useMemo<Record<SoilQualityGroup, string[]>>(() => ({
      [SoilQualityGroup.JB_1_2]: ["Grovsandet jord", "Finsandet jord"],
      [SoilQualityGroup.JB_3_4]: ["Grov lerblandet sandjord", "Fin lerblandet sandjord"],
      [SoilQualityGroup.JB_5_6]: ["Grov sandblandet lerjord", "Fin sandblandet lerjord"],
      [SoilQualityGroup.JB_7_10]: ["Lerjord", "Svær lerjord", "Meget svær lerjord", "Siltjord"],
      [SoilQualityGroup.JB_11]: ["Humusjord"],
   }), []);
   const baselineSoilGroup = SoilQualityGroup.JB_5_6;
   const baselineSoilFactor = soilFactorsByGroup[baselineSoilGroup] ?? 1;
   const getSoilGroupTooltipText = (soilGroup: SoilQualityGroup): string => {
      const soilTypes = (soilTypesByGroup[soilGroup] ?? []).join(", ");
      const groupFactor = soilFactorsByGroup[soilGroup] ?? 0;
      const soilPct = baselineSoilFactor > 0 ? (groupFactor / baselineSoilFactor) * 100 : Number.POSITIVE_INFINITY;
      return Localization.getFormattedText("info:soilGroupStat", {
         ...dynamicFactorTextArgs,
         soilTypes,
         soilPct: Number.isFinite(soilPct) ? formatFactor(soilPct) : "∞",
         baselineGroup: baselineSoilGroup
      });
   };
   const [soilExpanded, setSoilExpanded] = useState(false);

   const checkedData = useMemo(() => {
      return props.data.filter(d => props.checkedItems.has(d.documentID));
   }, [props.data, props.checkedItems]);

   const statistics = useMemo(() => {
      if (!checkedData || checkedData.length === 0) {
         return null;
      }

      const roundToQuarter = (value: number | null): number | null => {
         if (value === null) return null;
         return Math.round(value * 4) / 4;
      };

      if (props.priceMode === PriceMode.Standard) {
         const indexedValues = getSortedValuesForSeries(checkedData, "indexed", { averageCalculationMethod: props.averageCalculationMethod });
         const indexedStats = calculateWeightedStatisticsForSeries(checkedData, "indexed", "meanInterval", { averageCalculationMethod: props.averageCalculationMethod });

         return {
            indexed: {
               lower: roundToQuarter(indexedStats?.lower ?? null),
               middle: indexedStats?.middle ?? null,
               upper: roundToQuarter(indexedStats?.upper ?? null),
               count: indexedValues.length
            },
            soilPrices: null
         };
      } else {
         const effectiveMarkValues = getSortedValuesForSeries(checkedData, "effectiveMark", { delta: nonAgriToAgriRatio, averageCalculationMethod: props.averageCalculationMethod });
         const effectiveMarkStats = calculateWeightedStatisticsForSeries(checkedData, "effectiveMark", "meanInterval", { delta: nonAgriToAgriRatio, averageCalculationMethod: props.averageCalculationMethod });
         const soilPrices = calculateSoilSpecificAgriculturalPrices(checkedData, nonAgriToAgriRatio, soilFactorsByGroup, props.averageCalculationMethod);

         return {
            effectiveMark: {
               lower: roundToQuarter(effectiveMarkStats?.lower ?? null),
               middle: effectiveMarkStats?.middle ?? null,
               upper: roundToQuarter(effectiveMarkStats?.upper ?? null),
               count: effectiveMarkValues.length
            },
            soilPrices: soilPrices
         };
      }
   }, [checkedData, props.priceMode, nonAgriToAgriRatio, soilFactorsByGroup, props.averageCalculationMethod]);

   const totalTransactions = props.data.length;

   const [tooltipOpen, setTooltipOpen] = useState(false);

   const { refs: tooltipRefs, floatingStyles: tooltipFloatingStyles, context: tooltipContext } = useFloating({
      whileElementsMounted: autoUpdate,
      open: tooltipOpen,
      middleware: [offset(5)],
      onOpenChange: setTooltipOpen,
   });
   const { getFloatingProps: getTooltipFloatingProps } = useInteractions([
      useHover(tooltipContext),
      useDismiss(tooltipContext)
   ]);

   if (!statistics) {
      return (
         <div className="jpb-stats-root">
            <div className="jpb-stats-empty">
               {Localization.getText("No data available")}
            </div>
         </div>
      );
   }



   const tooltipContent = useMemo(() => {
      if (props.priceMode === PriceMode.Standard) {
         return (
            <div>
               <div style={{ fontWeight: "bold", marginBottom: "4px" }}>
                  {isIndexApplied
                     ? Localization.getFormattedText(
                        props.areaUnit === AreaUnit.Hectare ? "Indexed price per ha ({source})" : "Indexed price per m² ({source})",
                        { source: sourceLabel }
                     )
                     : indexedStatsLabel}
               </div>
               <div style={{ fontSize: "0.9em", lineHeight: "1.4" }}>
                  {isIndexApplied
                     ? Localization.getFormattedText("info:indexedStat", { source: sourceLabel })
                     : Localization.getText("info:nonIndexedStat")}
               </div>
               <div style={{ marginTop: "6px", fontSize: "0.85em", color: "#666" }}>
                  {Localization.getFormattedText("Based on {included} out of {total} transactions", { included: statistics?.indexed?.count ?? 0, total: totalTransactions })}
               </div>
            </div>
         );
      } else {
         return (
            <div>
               <div style={{ fontWeight: "bold", marginBottom: "4px" }}>
                  {props.areaUnit === AreaUnit.Hectare ? Localization.getText("Effective mark price per ha") : Localization.getText("Effective mark price per m²")}
               </div>
               <div style={{ fontSize: "0.9em", lineHeight: "1.4" }}>
                  {Localization.getFormattedText("info:effectiveMarkStat", dynamicFactorTextArgs)}
               </div>
               <div style={{ marginTop: "6px", fontSize: "0.85em", color: "#666" }}>
                  {Localization.getFormattedText("Based on {included} out of {total} transactions", { included: statistics?.effectiveMark?.count ?? 0, total: totalTransactions })}
               </div>
            </div>
         );
      }
   }, [props.priceMode, props.areaUnit, statistics, totalTransactions, dynamicFactorTextArgs, sourceLabel, isIndexApplied, indexedStatsLabel]);

   // Calculate date range same as in JPBTable
   const dateRange = useMemo(() => {
      const metadata = JPBManager.getDatabaseMetadata();

      const toDate = props.calculationDateTo
         ? new Date(props.calculationDateTo)
         : metadata?.uploadedAt
            ? new Date(metadata.uploadedAt)
            : (props.data.length > 0
               ? props.data.reduce((maxDate, prop) => (prop.transactionDate > maxDate ? prop.transactionDate : maxDate), props.data[0].transactionDate)
               : new Date());

      const fromDate = new Date(toDate);
      fromDate.setFullYear(fromDate.getFullYear() - props.yearGrouping);

      return {
         fromDate,
         toDate
      };
   }, [props.data, props.yearGrouping, props.calculationDateTo]);

   const columnLabels = useMemo(() => getStatisticsLabels("meanInterval"), []);
   const canExpandSoil = props.priceMode === PriceMode.Landbrug && Boolean(statistics?.soilPrices && Object.keys(statistics.soilPrices).length > 0);

   return (
      <div className="jpb-stats-root">
         <div className="jpb-card" style={{ overflow: "hidden" }}>
            {props.priceMode === PriceMode.Standard ? (
               <div className="jpb-stats-row">
                  <div className="jpb-stats-row-label">
                     <span style={{ display: "inline" }}>
                        <span style={{ fontWeight: "500", fontSize: "0.9em" }}>
                           {indexedStatsLabel}
                        </span>
                        <sup
                           ref={tooltipRefs.setReference}
                           style={{ cursor: "help", marginLeft: "4px", lineHeight: 1, fontSize: "12px", color: "#666" }}
                        >
                           <MIBIcon icon="info" />
                        </sup>
                        <sub style={{ fontSize: "0.65em", color: "#666", opacity: 0.75, marginLeft: "6px", position: "relative", top: "0.35em" }}>
                           {Localization.getText("Based on")} {statistics?.indexed?.count ?? 0} {Localization.getText("transactions")} ({formatDate(dateRange.fromDate)} - {formatDate(dateRange.toDate)})
                        </sub>
                     </span>
                  </div>
                  <div className="jpb-stats-row-values">
                     <div style={{ textAlign: "right", minWidth: "80px" }}>
                        <div style={{ color: "#666", fontSize: "0.8em" }}>
                           {columnLabels.lower}
                        </div>
                        <div>{statistics?.indexed?.lower !== null ? formatPricePerSquareMeter(statistics.indexed!.lower, props.areaUnit) : "-"}</div>
                     </div>
                     <div style={{ textAlign: "right", minWidth: "80px" }}>
                        <div style={{ color: "#666", fontSize: "0.8em" }}>
                           {columnLabels.middle}
                        </div>
                        <div style={{ fontWeight: "600" }}>{statistics?.indexed?.middle !== null ? formatPricePerSquareMeter(statistics.indexed!.middle, props.areaUnit) : "-"}</div>
                     </div>
                     <div style={{ textAlign: "right", minWidth: "80px" }}>
                        <div style={{ color: "#666", fontSize: "0.8em" }}>
                           {columnLabels.upper}
                        </div>
                        <div>{statistics?.indexed?.upper !== null ? formatPricePerSquareMeter(statistics.indexed!.upper, props.areaUnit) : "-"}</div>
                     </div>
                  </div>
               </div>
            ) : (
               <div
                  className="jpb-stats-row"
                  style={{
                     cursor: canExpandSoil ? "pointer" : "default",
                     userSelect: "none"
                  }}
                  onClick={() => {
                     if (canExpandSoil) setSoilExpanded(v => !v);
                  }}
               >
                  <div className="jpb-stats-row-label">
                     <span style={{ display: "inline" }}>
                        <span style={{ fontWeight: "500", fontSize: "0.9em" }}>
                           {props.areaUnit === AreaUnit.Hectare ? Localization.getText("Effective mark price per ha") : Localization.getText("Effective mark price per m²")}
                        </span>
                        <sup
                           ref={tooltipRefs.setReference}
                           style={{ cursor: "help", marginLeft: "4px", lineHeight: 1, fontSize: "12px", color: "#666" }}
                        >
                           <MIBIcon icon="info" />
                        </sup>
                        <sub style={{ fontSize: "0.65em", color: "#666", opacity: 0.75, marginLeft: "6px", position: "relative", top: "0.35em" }}>
                           {Localization.getText("Based on")} {statistics?.effectiveMark?.count ?? 0} {Localization.getText("transactions")} ({formatDate(dateRange.fromDate)} - {formatDate(dateRange.toDate)})
                        </sub>
                     </span>
                  </div>
                  <div className="jpb-stats-row-values">
                     <div style={{ textAlign: "right", minWidth: "80px" }}>
                        <div style={{ color: "#666", fontSize: "0.8em" }}>
                           {columnLabels.lower}
                        </div>
                        <div>{statistics?.effectiveMark?.lower !== null && statistics.effectiveMark ? formatPricePerSquareMeter(statistics.effectiveMark.lower, props.areaUnit) : "-"}</div>
                     </div>
                     <div style={{ textAlign: "right", minWidth: "80px" }}>
                        <div style={{ color: "#666", fontSize: "0.8em" }}>
                           {columnLabels.middle}
                        </div>
                        <div style={{ fontWeight: "600" }}>{statistics?.effectiveMark?.middle !== null && statistics.effectiveMark ? formatPricePerSquareMeter(statistics.effectiveMark.middle, props.areaUnit) : "-"}</div>
                     </div>
                     <div style={{ textAlign: "right", minWidth: "80px" }}>
                        <div style={{ color: "#666", fontSize: "0.8em" }}>
                           {columnLabels.upper}
                        </div>
                        <div>{statistics?.effectiveMark?.upper !== null && statistics.effectiveMark ? formatPricePerSquareMeter(statistics.effectiveMark.upper, props.areaUnit) : "-"}</div>
                     </div>
                     {canExpandSoil && (
                        <div style={{ display: "flex", alignItems: "center", marginLeft: "12px", color: "#666", fontSize: "2.2em", lineHeight: "1", height: "1.2em" }}>
                           {soilExpanded ? "▾" : "▸"}
                        </div>
                     )}
                  </div>
               </div>
            )}
            {Boolean(tooltipContent && tooltipOpen) && (
               <MITFloatingPortal>
                  <div
                     ref={tooltipRefs.setFloating}
                     className="ViewButtonToolTip assumptions-box"
                     style={{
                        ...tooltipFloatingStyles,
                        maxWidth: "300px",
                        fontSize: "0.85em"
                     }}
                     {...getTooltipFloatingProps()}
                  >
                     {tooltipContent}
                  </div>
               </MITFloatingPortal>
            )}

            {soilExpanded && props.priceMode === PriceMode.Landbrug && statistics?.soilPrices && Object.keys(statistics.soilPrices).length > 0 && (
               <div style={{ padding: "4px 0 8px 18px" }}>
                  {SOIL_QUALITY_GROUPS
                     .filter(group => statistics.soilPrices?.[group] !== undefined)
                     .map((soilGroup) => {
                        const price = statistics.soilPrices?.[soilGroup];
                        if (price === undefined) return null;
                        return (
                           <SoilRowWithTooltip
                              key={soilGroup}
                              soilGroup={soilGroup}
                              price={price}
                              areaUnit={props.areaUnit}
                              tooltipText={getSoilGroupTooltipText(soilGroup)}
                           />
                        );
                     })}
               </div>
            )}
         </div>
      </div>
   );
}

export default JPBStatistics;

